import { ApiOkResponse, ApiResponse } from 'apisauce';

import { MOCK } from '../../../configDebug';
import { ams, fundamental } from '../../__offline-data__';
import pdfJson from '../../__offline-data__/file/pdf.json';
import { OrderSideEnum, OrderStatusEnum } from '../../enums';
import { InboxMessage, InboxPageResponse, SummaryResponse, UnreadCountResponse } from '../../models/ams/types';
import { PagedResponse, SimplePagedResponse } from '../../models/common-response-types';
import { DEFAULT_PAGE_RESPONSE } from '../../models/pageable';
import {
  AutoPassRequest,
  AutoPassResponse,
  CouponPreviewParams,
  CouponPreviewResponse,
  CreatePlanPaymentParams,
  CreatePlanPaymentResponse,
  CreatePurchasePaymentParam,
  ModifyOwnBirthdateRequest,
  AccountSummary,
  Payment,
  PaymentsFilter,
  SearchTradingPlansRequest,
  TradingPlan,
} from '../../models/crm/types';
import {
  AvailableIndexesResponse,
  BalanceSheet,
  CashFlow,
  DividendPayerData,
  Earning,
  EpicMoversParamType,
  EpicMoversResponse,
  ETF,
  ETFDetailParams,
  ETFDetailResponse,
  ETFParams,
  GapperParamType,
  GapperResponse,
  HighGrowthCompanyData,
  Income,
  LiquidityAndSolvencyRatiosResponse,
  NewStockData,
  PageableFilterRequest,
  PopularStock,
  ProfitabilityRatiosResponse,
  RatiosByTypeParamType,
  RatiosResponse,
  Sector,
  SectorDetail,
  SectorDetailParams,
  StatementApiResponse,
  StatementSearchParamType,
  ValuationRatiosResponse,
  Week52HighLowParamType,
  Week52HighLowResponse,
  Week52HighLowTopListParamType,
  Week52HighLowTopListResponse,
  MarketCapData,
  MarketCapsRequest,
} from '../../models/fundamental/types';
import { GWMarketDataSummaryBucket, GWQueryCase } from '../../models/gateway/types';
import { CryptoLinkedAccountRequestData, LinkedAccountRequestData } from '../../models/linked-account/types';
import { SubscriptionTopic } from '../../models/market-data/types';
import { GetCryptoRateParams, GetCryptoRateResponse } from '../../models/payment/types';
import { PeopleAlsoTradeItem, PeopleAlsoTradeParams } from '../../models/reporting/types';
import { StockDescriptor } from '../../models/stock-descriptor';
import { GetDetailsFunctionalityType, Order, QueriesCacheType } from '../../models/trading/types';
import { gatewayConnected, sorOrderEvent } from '../../store/common-actions';
import { UserPreferencesRequest } from '../../store/file/types';
import { SearchEarningsPayload } from '../../store/fundamental/types';
import { createSuccessResponse } from '../../store/helpers';
import { connected as marketDataConnected } from '../../store/market-data';
import { EMPTY_CALCULATED_PRICE_DATA, EMPTY_PRICE_DATA } from '../../store/market-data/priceSource.helpers';
import { InstrumentSnapshotMap } from '../../store/market-data/types';
import {
  AccountTradeHistoryPayloadData,
  GetAccountsAvailableCashRequest,
  GetAccountTradeHistoryFirstAndLastTradePayload,
  GetMyAccountChartDataPayload,
  OpenPositionsPayloadData,
  OrderDetailPayloadData,
} from '../../store/reporting/types';
import { ReportingFilePayloadData } from '../../store/reportingFile/types';
import { connected as tradingConnected } from '../../store/trading';
import { updateBigChartCacheOnLoadChartDataCompleted } from '../../store-util/BigChartCache';
import { updateInstrumentSnapshotCache } from '../../store-util/InstrumentSnapshotCache';
import { DEBUG_MARKET_STATE } from '../../store-util/MarketStateCache';
import { _setNBBOPricesCache_InUnitTestsAndMockedMode } from '../../store-util/NBBOPriceCache';
import { updateSmallChartDataCache } from '../../store-util/SmallChartDataCache';
import { updateStatsCache } from '../../store-util/StatsCache';
import {
  _setTradePricesCache_InUnitTestsAndMockedMode,
  updateTradePriceCacheFromInstrumentSnapshot,
} from '../../store-util/TradePriceCache';
import { tradingConfig } from '../../trading-settings';
import { newPromiseWithReduxCacheData } from '../../util/DataHelpers';
import { parseRows } from '../../util/GatewayHelpers';
import { dataArrayToMap, processMDQueryData, processOHLCVResponse } from '../../util/MarketDataHelpers';
import { getDateTime } from '../../util/temporalDeadZoneFunctions';
import { NullableNumber, NullableString } from '../../util/types';
import { MarketDataClientStatus, TradeClientStatus } from '../client-status';
import { AccountCloseoutResponse } from '../closeout/types';
import {
  IAMSService,
  IAuthService,
  ICloseoutService,
  ICommonAuthService,
  ICRMService,
  IFileService,
  IFundamentalService,
  IMarketDataService,
  IPaymentService,
  IReportingFileService,
  IReportingService,
  ITradingService,
} from '../types';
import { GatewayRequestType } from '../web-socket/types';

import MockedOMSAndMDS from './MockedOMSAndMDS';
import {
  calculateDelay,
  generateOfflineResponse,
  generateOHLCVSequence,
  getOkResponse,
  newPromise,
  newPromiseForData,
} from './offline-helpers';
import { MOCKED_CHART_POINTS } from './offlineSettings';

export class OfflineAuthService implements IAuthService, ICommonAuthService {
  authorize(configuration: any) {
    if (MOCK.TRADING_MODE === 'static') {
      _setTradePricesCache_InUnitTestsAndMockedMode({
        AAPL: {
          currentPrice: 123.09,
          prevPrice: 114.93,
          symbol: 'AAPL',
          currentPriceDate: '2022-04-02',
          change: '0.02',
          changePercent: '0.39',
          direction: '',
          priceData: EMPTY_PRICE_DATA,
          calculatedSymbolPrices: EMPTY_CALCULATED_PRICE_DATA,
          changePercentWithSign: '',
        },
        TSLA: {
          currentPrice: 1101.09,
          prevPrice: 1099.93,
          symbol: 'TSLA',
          currentPriceDate: '2022-04-01',
          change: '0.12',
          changePercent: '0.02',
          direction: '',
          priceData: EMPTY_PRICE_DATA,
          calculatedSymbolPrices: EMPTY_CALCULATED_PRICE_DATA,
          changePercentWithSign: '',
        },
      });
      _setNBBOPricesCache_InUnitTestsAndMockedMode({
        AAPL: { askprice: 123.09, bidprice: 114.93, symbol: 'AAPL' },
        TSLA: { askprice: 1101.09, bidprice: 1099.93, symbol: 'TSLA' },
      });
    }
    DEBUG_MARKET_STATE.enable();
    return newPromise('auth/authorize');
  }
  signout() { return newPromise('empty'); }
  getUserManager() { return {} as any; }
  // common service
  registerUser() { return newPromise('auth/registerUser'); }
  confirmEmail(email: string) { return newPromise('auth/confirmEmail'); }
  async redeemSignupToken(username: string, signupToken: string, returnUrl?: string): Promise<void> {
    await newPromise('auth/redeemSignupToken');
  }
  resendEmail(email: string, first_name: NullableString, last_name: NullableString, country_of_residence: NullableString) { return newPromise('auth/resendEmail'); }
  sendPhoneCode(userId: NullableNumber, phone: string) { return newPromise('auth/sendPhoneCode'); }
  verifyPhoneNumber(userId: NullableNumber, code: string) { return newPromise('auth/verifyPhoneNumber'); }
  sendResetPassword(email: string, client: string, return_url: string) { return newPromise('empty'); }
  sendNonAppropriatenessEmail(email: string) { return newPromise('crm/sendNonAppropriatenessEmail'); }
}

export class OfflinePaymentService implements IPaymentService {
  api = {} as any;
  getCardTransferStatus() { return newPromise('empty'); }
  getCryptoRate(_params?: GetCryptoRateParams) { return newPromise('empty') as Promise<ApiResponse<GetCryptoRateResponse>>; }
}

export class OfflineCRMService implements ICRMService {
  api = {} as any;
  getAccountsSummary(): Promise<ApiResponse<AccountSummary[]>> {
    const data: AccountSummary[] = [];
    return newPromiseForData(data);
  }
  searchTradingPlans(request: SearchTradingPlansRequest): Promise<ApiResponse<TradingPlan[]>> {
    return newPromise('crm/searchTradingPlans') as Promise<ApiResponse<TradingPlan[]>>;
  }
  getReferralOwnProfile(): Promise<any> {
    return newPromise('crm/getReferralOwnProfile');
  }
  getReferralOwnQr(): Promise<any> {
    return newPromise('crm/getReferralOwnQr');
  }
  getReferralOwnReferredUsers(): Promise<any> {
    return newPromise('crm/getReferralOwnReferredUsers');
  }
  getReferralOwnCommissions(): Promise<any> {
    return newPromise('crm/getReferralOwnCommissions');
  }
  createReferralOwnPayoutRequest(): Promise<any> {
    return newPromise('crm/createReferralOwnPayoutRequest');
  }
  getAutoPass(): Promise<ApiResponse<AutoPassResponse>> {
    return newPromise('crm/getAutoPass') as Promise<ApiResponse<AutoPassResponse>>;
  }
  updateAutoPass(request: AutoPassRequest): Promise<ApiResponse<AutoPassResponse>> {
    return newPromise('crm/updateAutoPass') as Promise<ApiResponse<AutoPassResponse>>;
  }
  getTradingPlatforms(): Promise<any> { return newPromise('empty'); }
  getCountryList() { return newPromise('crm/country'); }
  enrollGet(methodName: string, params: any[]) {
    if (methodName) {
      let callName: string = `${methodName[3].toLowerCase()}${methodName.substring(4)}`;
      return newPromise(`crm/${callName}`);
    }
    console.debug(`[OfflineCRMService::GET] Method name should be defined - methodName='${methodName}', params='${params}'`);
    throw new Error(`[OfflineCRMService::GET] Method name should be defined - methodName='${methodName}', params='${params}'`);
  }
  enrollPost(methodName: string, requestBody: any): Promise<any> {
    if (methodName) {
      let callName: string = `${methodName[4].toLowerCase()}${methodName.substring(5)}`;
      return newPromise(`crm/${callName}`);
    }
    console.debug(`[OfflineCRMService::POST] Method name should be defined - methodName='${methodName}'`, requestBody);
    throw new Error(`[OfflineCRMService::POST] Method name should be defined - methodName='${methodName}'`);
  }
  getPayments(filter: PaymentsFilter): Promise<ApiResponse<Payment[]>> {
    return newPromise('crm/getPayments') as Promise<ApiResponse<Payment[]>>;
  }
  activateDemoAccount(individualId: number): Promise<ApiResponse<void>> {
    return newPromise('crm/activateDemoAccount') as Promise<ApiResponse<void>>;
  }
  updateIndividualTransitionFromNewToForVerification(
    individualId: number,
  ): Promise<ApiResponse<void>> {
    return newPromise('crm/updateIndividualTransitionFromNewToForVerification') as Promise<ApiResponse<void>>;
  }
  getLinkedAccountsByIndividualId(accountId: number) {
    return newPromise('crm/linkedAccounts');
  }
  registerLinkedAccount(linkedAccountRequestData: LinkedAccountRequestData) {
    return newPromise('crm/registerLinkedAccount');
  }
  registerCryptoLinkedAccount(cryptoLinkedAccountRequestData: CryptoLinkedAccountRequestData) {
    return newPromise('crm/registerCryptoLinkedAccount');
  }
  getOwnerTradingAccounts(individualId: number): Promise<any> {
    return newPromise('crm/ownerTradingAccounts');
  }
  registerWithdrawalRequest(): Promise<any> {
    return newPromise('crm/registerWithdrawalRequest');
  }
  createPlanPayment(params: CreatePlanPaymentParams): Promise<ApiResponse<CreatePlanPaymentResponse>> {
    return newPromise('crm/createPlanPayment') as Promise<ApiResponse<CreatePlanPaymentResponse>>;
  }

  createPurchasePayment(params: CreatePurchasePaymentParam): Promise<ApiResponse<CreatePlanPaymentResponse>> {
    return newPromise('crm/createPurchasePayment') as Promise<ApiResponse<CreatePlanPaymentResponse>>;
  }

  couponPreview(params: CouponPreviewParams): Promise<ApiResponse<CouponPreviewResponse>> {
    return newPromise('crm/couponPreview') as Promise<ApiResponse<CouponPreviewResponse>>;
  }

  modifyOwnBirthdate(_request: ModifyOwnBirthdateRequest): Promise<ApiResponse<void>> {
    return newPromise('crm/modifyOwnBirthdate') as Promise<ApiResponse<void>>;
  }
}

let mockedOMSAndMDS: MockedOMSAndMDS;

export class OfflineTradingService implements ITradingService {
  dispatch: any;
  status: any;

  constructor(dispatch: any) {
    this.dispatch = dispatch;
    this.status = TradeClientStatus.INITIAL;
    if (MOCK.TRADING_MODE !== 'static') mockedOMSAndMDS = new MockedOMSAndMDS(dispatch);
  }

  isConnected() { return true; }

  connect() {
    this.status = TradeClientStatus.SOCKET_CONNECTED;
    this.dispatch(tradingConnected());
  }
  reconnect(): void { this.connect(); }
  disconnect(): void { }
  newOrder(order: Order): Promise<any> {
    let timeNow = getDateTime();
    let newOrderRequest = {
      ...tradingConfig,
      timeInForce: 'Day',
      messageType: 'neworder',
      side: OrderSideEnum.Buy,
      reqId: 'n/a in mock',
      ...order,
      status: OrderStatusEnum.InProgress,
      createdAt: timeNow,
      updatedAt: timeNow,
    };

    console.info('[MOCK] [T] newOrder request', newOrderRequest);

    if (MOCK.TRADING_MODE === 'static') {
      return newPromise('trading/newOrder', null, false, newOrderRequest);
    }

    mockedOMSAndMDS.neworder(newOrderRequest as any);
    return newPromise('trading/newOrder', false, false, newOrderRequest);
  }
  getDetails(account: string, functionality: GetDetailsFunctionalityType): Promise<any> {
    if (MOCK.TRADING_MODE === 'static') {
      return newPromise(`trading/${functionality}`);
    }

    switch (functionality) {
      case 'getBalances':
        return newPromise('trading/getBalances', false, false, mockedOMSAndMDS.getBalances());
      case 'getOrders':
        return newPromise('trading/getOrders', false, false, mockedOMSAndMDS.getOrders());
      case 'getOrderHistory':
        return newPromise('trading/getOrderHistory', false, false, mockedOMSAndMDS.getOrderHistory());
      case 'getPositions':
        return newPromise('trading/getPositions', false, false, mockedOMSAndMDS.getPositions());
      default:
        console.warn(`[OfflineGateway] Not known getDetails functionality - ${functionality}`);
    }

    return newPromise('empty');
  }
  getOrders(account: string): Promise<any> {
    return newPromise('trading/getOrders');
  }
  getPositions(account: string): Promise<any> {
    return newPromise('trading/getPositions');
  }
  cancelOrder(order: Order, account: string): Promise<any> {
    return newPromise('trading/cancelOrder');
  }
  getBuyingPowerFromOMS(account: string): Promise<any> {
    return newPromise('trading/getBalances');
  }
  modifyOrder(order: Order, account: string): Promise<any> {
    return newPromise('trading/modifyOrder');
  }
}

export class OfflineMarketDataService implements IMarketDataService {
  dispatch: any;
  status: any;
  ohlcv: any = {
    default: { queryCase: null, symbol: 'AAPL' },
    id: 0,
  };
  ohlcvCache: QueriesCacheType = {};

  constructor(dispatch: any) {
    this.dispatch = dispatch;
    this.status = MarketDataClientStatus.INITIAL;
  }
  connect() {
    this.status = MarketDataClientStatus.SUCCESSFUL;
    setTimeout(() => this.dispatch(marketDataConnected()), calculateDelay());
  }
  reconnect(): void { }
  disconnect(): void { }
  subscribe(topic: SubscriptionTopic, symbols: string[]): Promise<any> {
    mockedOMSAndMDS?.subscribe(symbols);
    return newPromise('empty');
  }
  unsubscribe(topic: SubscriptionTopic, symbols: string[]): Promise<any> {
    mockedOMSAndMDS?.unsubscribe(symbols);
    return newPromise('empty');
  }
  query(
    topic: string,
    symbols: string[],
    startTime?: string | Date,
    endTime?: string | Date,
    bucket?: any,
    queryCase?: any,
  ) {
    const { ohlcv, ohlcvCache } = this;
    let data;

    switch (topic) {
      case 'ohlcv':
        data = generateOfflineResponse('market/ohlcv');
        ohlcvCache[ohlcv.id] = ohlcv.default;
        ohlcvCache[ohlcv.id].queryCase = queryCase;
        data.ref = ohlcv.id;
        ohlcv.id++;
        break;
      case 'nbbo':
        data = generateOfflineResponse('market/nbbo');
        break;
      default:
        throw new Error(`[MOCK] [MD] Query '${topic}' not implemented`);
    }
    processOHLCVResponse(this.dispatch, data, this.ohlcvCache);
  }
  async loadTopic(topic: string) {
    switch (topic) {
      case 'ohlcv': {
        const ohlcv = await newPromise('market/ohlcv');
        // eslint-disable-next-line no-promise-executor-return
        return new Promise(success => true);
      }

      case 'nbbo': {
        const nbboData = await newPromise('market/nbbo');
        // eslint-disable-next-line no-promise-executor-return
        return new Promise(success => true);
      }

      default:
        throw new Error(`[MOCK] Unknown topic '${topic}' for MD::subscribe`);
    }
  }
}

export class OfflineCloseoutService implements ICloseoutService {
  getAccountCloseoutStatus(accountReferenceId: string): Promise<ApiResponse<AccountCloseoutResponse>> {
    return newPromiseForData({} as AccountCloseoutResponse);
  }
  streamAccountEvents(
    accountRefId: string,
    onEvent: (event: any) => void,
    onError?: (error: Error) => void,
    onComplete?: () => void,
  ): () => void {
    console.log('[MOCK] streamAccountEvents called for accountRefId:', accountRefId);
    // Return a no-op cleanup function for mock mode
    return () => {
      console.log('[MOCK] streamAccountEvents cleanup called');
    };
  }
}

export class OfflineFundamentalService implements IFundamentalService {
  async searchCompanies(query: string): Promise<any> {
    const path = 'fundamental/search';
    let result = generateOfflineResponse(path);
    result.data = result?.data.filter((item: StockDescriptor) => (
      item?.symbol.toLowerCase().includes(query.toLowerCase())
      || item?.companyName.toLowerCase().includes(query.toLowerCase())
    ));

    return newPromise(path, false, false, result);
  }
  getCompany(symbol: string) {
    return newPromise('fundamental/company');
  }

  getCompanyLogoAndName(symbols: string): Promise<any> {
    const data = fundamental.companyLogoAndName;

    const symbolsArray = symbols?.split(',') || [];
    symbolsArray.forEach(symbol => {
      if (data.filter((item: any) => item.symbol === symbol).length === 0) {
        const newItem = { ...data[0] };
        newItem.symbol = symbol;
        data.push(newItem);
      }
    });

    return newPromise('fundamental/companyLogoAndName', false, false);
  }
  searchEarnings(params?: SearchEarningsPayload) {
    const data: PagedResponse<Earning> = require('../../__offline-data__/fundamental/earningsSearch.json');
    return newPromiseForData(data);
  }
  getKeyStats(symbol: string) {
    return newPromise('fundamental/stats');
  }
  getPopularStocks(constrained: boolean, count: number): Promise<ApiResponse<PopularStock[]>> {
    const data: PopularStock[] = require('../../__offline-data__/fundamental/popularStocks.json');
    return newPromiseForData(data);
  }

  getNewStocks() {
    const data: NewStockData[] = require('../../__offline-data__/fundamental/newStocks.json');
    return newPromiseForData(data);
  }

  getDividendPayers(request?: PageableFilterRequest) {
    const data: DividendPayerData[] = require('../../__offline-data__/fundamental/dividendPayers.json');
    return newPromiseForData(data);
  }
  getHighGrowthCompanies(request?: PageableFilterRequest) {
    const data: HighGrowthCompanyData[] = require('../../__offline-data__/fundamental/highGrowtCompanies.json');
    return newPromiseForData(data);
  }
  getMarketIndexes(symbols?: string[]) {
    const data: [] = require('../../__offline-data__/fundamental/marketIndexes.json');
    return newPromiseForData(data);
  }
  getEpicMovers(
    params: EpicMoversParamType,
  ): Promise<ApiResponse<EpicMoversResponse>> {
    return newPromiseForData([] as any);
  }

  getMarketCaps(
    params: MarketCapsRequest,
  ): Promise<ApiResponse<SimplePagedResponse<MarketCapData>>> {
    return newPromiseForData({ ...DEFAULT_PAGE_RESPONSE, content: [] });
  }

  getSectors(): Promise<ApiResponse<Sector[]>> {
    const data: Sector[] = fundamental.sectors;
    return newPromiseForData(data);
  }

  getSectorDetail(params: SectorDetailParams): Promise<ApiResponse<SectorDetail>> {
    const data: SectorDetail = fundamental.sector;
    data.sectorId = params.sectorId;
    return newPromiseForData(data);
  }

  getETFs(params: ETFParams): Promise<ApiResponse<PagedResponse<ETF>>> {
    const data: PagedResponse<ETF> = fundamental.ETFData;
    return newPromiseForData(data);
  }

  getETFDetails(params: ETFDetailParams): Promise<ApiResponse<PagedResponse<ETFDetailResponse>>> {
    const data: PagedResponse<ETFDetailResponse> = fundamental.etfDetails;
    return newPromiseForData(data);
  }

  getStatements(params: StatementSearchParamType): Promise<ApiResponse<StatementApiResponse>> {
    return newPromiseForData({} as any);
  }

  getNonWorkingDaysAndMarketTime(country: any): Promise<any> {
    return newPromiseForData({} as any);
  }

  getNonWorkingDaysAndMarketTimeMultiple(countries: any): Promise<any> {
    return newPromiseForData({} as any);
  }

  getStatementsCashFlow(params: StatementSearchParamType): Promise<ApiResponse<CashFlow[]>> {
    return newPromiseForData([] as any);
  }

  getStatementsBalanceSheet(params: StatementSearchParamType): Promise<ApiResponse<BalanceSheet[]>> {
    return newPromiseForData([] as any);
  }

  getStatementsIncome(params: StatementSearchParamType): Promise<ApiResponse<Income[]>> {
    return newPromiseForData([] as any);
  }

  getGappers(params: GapperParamType): Promise<ApiResponse<SimplePagedResponse<GapperResponse>>> {
    return newPromiseForData([] as any);
  }

  getWeek52HighLow(params: Week52HighLowParamType): Promise<ApiResponse<SimplePagedResponse<Week52HighLowResponse>>> {
    return newPromiseForData([] as any);
  }

  getWeek52HighLowTopList(
    params: Week52HighLowTopListParamType,
  ): Promise<ApiResponse<Week52HighLowTopListResponse>> {
    return newPromiseForData([] as any);
  }

  getAllRatios(
    params: RatiosByTypeParamType,
  ): Promise<ApiResponse<RatiosResponse>> {
    return newPromiseForData([] as any);
  }

  getRatiosValuation(
    params: RatiosByTypeParamType,
  ): Promise<ApiResponse<ValuationRatiosResponse>> {
    return newPromiseForData([] as any);
  }

  getRatiosProfitability(
    params: RatiosByTypeParamType,
  ): Promise<ApiResponse<ProfitabilityRatiosResponse>> {
    return newPromiseForData([] as any);
  }

  getRatiosLiquidityAndSolvency(
    params: RatiosByTypeParamType,
  ): Promise<ApiResponse<LiquidityAndSolvencyRatiosResponse>> {
    return newPromiseForData([] as any);
  }

  getAvailableIndexes(): Promise<ApiResponse<AvailableIndexesResponse[]>> {
    return newPromiseForData([] as any);
  }

}

export class OfflineNewsService {
}

export class OfflineReportingService implements IReportingService {
  getPaymentsAccountsIntividual(
    configuration: {
      page_size: number,
      page_number: number,
      order_column: string,
      payment_method: number,
      transaction_overview_day: number
    },
  ) {
    return newPromise('reporting/paymentsAccountsIntividual');
  }
  executions() {
    return newPromise('reporting/executions');
  }
  getAccountsAvailableCash(requstBody: GetAccountsAvailableCashRequest) {
    return newPromise('crm/getAccountsAvailableCash');
  }
  getNetEquityComponents(requstBody: GetMyAccountChartDataPayload) {
    return newPromise('reporting/getNetEquityComponents');
  }
  getOrderDetailData(requstBody: OrderDetailPayloadData) {
    return newPromise('reporting/getOrderDetailData');
  }
  getAccountTradeHistory(requstBody: AccountTradeHistoryPayloadData) {
    return newPromise('reporting/getAccountTradeHistory');
  }
  getOpenPositionsData(requstBody: OpenPositionsPayloadData) {
    return newPromise('reporting/getOpenPositionsData');
  }
  getPeopleAlsoTradeData(params: PeopleAlsoTradeParams) {
    const data: PeopleAlsoTradeItem[] = require('../../__offline-data__/reporting/peopleAlsoTrade.json');
    return newPromiseForData(data);
  }
  getBPInClosedMarket(requstBody: { accountReferenceId: string }) {
    return newPromiseForData({ accountReferenceId: 'test', buyingPowerId: 9 });
  }
  getAccountTradeHistoryFirstAndLastTrade(requstBody: GetAccountTradeHistoryFirstAndLastTradePayload) {
    return newPromise('reporting/getAccountTradeHistoryFirstAndLastTrade') as any;
  }
}

export class OfflineReportingFileService implements IReportingFileService {
  get(requstBody: ReportingFilePayloadData) {
    // eslint-disable-next-line no-promise-executor-return
    return new Promise<ApiOkResponse<InboxPageResponse>>((res, rej) => newPromise('download-order-confirmation-report'));
  }
}

export class OfflineFileService implements IFileService {
  get(fileName: string) {
    const parts = fileName.split('.');
    if (parts.length === 2 && parts[1].toLowerCase() === 'pdf') {
      return new Promise<ApiResponse<string>>((success, reject) => {
        setTimeout(() => success(getOkResponse(pdfJson.pdf)), 1000);
      });
    }
    const response = createSuccessResponse('');

    return newPromise('file/get', null, false, response) as Promise<ApiResponse<string>>;
  }

  uploadUserPreferenceFile(data: UserPreferencesRequest) { return newPromise('file/uploadUserPreferenceFile'); }

  getUserPreferenceFile(preferenceName: string) { return newPromise('file/userPreferenceFile'); }

  deleteByName(fileName: string) {
    const response = createSuccessResponse('');

    return newPromise('file/deleteByName', false, false, response) as Promise<ApiResponse<null>>;
  }
}

export class OfflineAmsService implements IAMSService {
  getAllMessages() {
    // eslint-disable-next-line no-promise-executor-return
    return new Promise<ApiOkResponse<InboxPageResponse>>((res, rej) => newPromise('ams/getAllMessages'));
  }
  deleteMessages(ids: number | number[]) {
    // eslint-disable-next-line no-promise-executor-return
    return new Promise<ApiOkResponse<void>>((res, rej) => newPromise('ams/voidResponse'));
  }
  getMessage(id: number) {
    // eslint-disable-next-line no-promise-executor-return
    return new Promise<ApiOkResponse<InboxMessage>>((res, rej) => newPromise('ams/getMessage'));
  }
  markAllMessagesAsRead() {
    // eslint-disable-next-line no-promise-executor-return
    return new Promise<ApiOkResponse<void>>((res, rej) => newPromise('ams/voidResponse'));
  }
  markAsRead(ids: number | number[]) {
    // eslint-disable-next-line no-promise-executor-return
    return new Promise<ApiOkResponse<void>>((res, rej) => newPromise('ams/voidResponse'));
  }
  notifyUnreadCount() {
    const data: UnreadCountResponse = ams.notifyUnreadCountResponse;
    return newPromiseForData(data);
  }
  markAsReadByMetadata() {
    // eslint-disable-next-line no-promise-executor-return
    return new Promise<ApiOkResponse<void>>((res, rej) => newPromise('ams/voidResponse'));
  }
  // eslint-disable-next-line no-promise-executor-return
  getSummary = () => new Promise<ApiOkResponse<SummaryResponse>>((res, rej) => newPromise('ams/getSummary.json'));
  streamUserEvents(handlers: {
    onEvent: (event: any) => void,
    onOpen?: () => void,
    onError?: (error: Error) => void,
    onComplete?: () => void,
  }): () => void {
    console.log('[MOCK] streamUserEvents called');
    // Return a no-op cleanup function for mock mode
    return () => {
      console.log('[MOCK] streamUserEvents cleanup called');
    };
  }
}

class GatewayMock {
  dispatch: any;
  name: string = 'GW-WS-MOCK';
  public static connected = false;
  constructor(dispatch: any) {
    this.dispatch = (action: any, delay: 0) => setTimeout(() => dispatch(action), delay * 1000);
    if (dispatch == null) this.name = 'GW-Rest-MOCK';
  }
}
const gatewayProxy = {
  get(target: any, prop: string, receiver: any) {
    const func = (function offlineGatewayFunc(...args: any[]) {
      let data;
      switch (prop) {
        case 'disconnect':
        case 'reconnect':
          // these do not need implementation so far
          // added here to not produce error in default case
          break;

        case 'connect': {
          const isReconnect = !!args[1];
          const isConnectDisabled = isReconnect ? !!MOCK.GATEWAY?.disableReconnect : !!MOCK.GATEWAY?.disableConnect;
          if (isConnectDisabled) {
            console.warn(`[${target.name}] Skipping ${isReconnect ? 're' : ''}connect ... (see configDebug => MOCK.GATEWAY)`, args);
          } else {
            GatewayMock.connected = true; // eslint-disable-line no-param-reassign
            target.dispatch(gatewayConnected());
          }
          break;
        }

        case 'sendRequest': {
          const requestType: GatewayRequestType = args[0];
          const requestName: string = requestType;
          const requestData: any = args[1];
          const queryCase: GWQueryCase = args[2];
          const queryName: NullableString = queryCase != null ? ` / ${GWQueryCase[queryCase] ?? queryCase}` : '';
          const symbol = ((requestData?.symbols || [])[0] || requestData.symbol);
          const symbols: string[] = requestData?.symbols ?? (requestData.symbol ?? []);

          console.log(`[${target.name}] Request ${requestName} (${requestType})${queryName}`);
          console.debug(`[${target.name}] Request ${requestName} full body (${requestType})${queryName}`, args);

          if (!GatewayMock.connected && requestType !== GatewayRequestType.Login) {
            console.warn(`[${target.name}] Skipping request ${requestName} (${requestType})  -- not connected --   (see configDebug => MOCK.GATEWAY)`);
            return;
          }

          let result;

          switch (requestType) {
            case GatewayRequestType.Login:
              target.dispatch(gatewayConnected());
              break;

            case GatewayRequestType.MarketDataQuery:
              if (MOCK.TRADING_MODE === 'static') {
                data = require('../../__offline-data__/market-data/CRSR_days.json');
              } else {
                data = generateOHLCVSequence(
                  'AAPL',
                  [ 100.01, 200.01 ],
                  [ 100.01, 200.01 ],
                  [ 100.01, 200.01 ],
                  [ 100.01, 200.01 ],
                  [ 100, 1000000 ],
                  1000,
                );
              }
              processMDQueryData((action: any) => target.dispatch(action, 1), { symbol, queryCase }, data);
              break;

            case GatewayRequestType.MarketDataSubscribeTrade:
            case GatewayRequestType.MarketDataSubscribeNbbo:
              mockedOMSAndMDS?.subscribe(symbols);
              break;

            case GatewayRequestType.MarketDataUnsubscribeTrade:
            case GatewayRequestType.MarketDataUnsubscribeNbbo:
              mockedOMSAndMDS?.unsubscribe(symbols);
              break;

            case GatewayRequestType.GetLatestNewsArticles:
              data = generateOfflineResponse('gateway/news');
              break;

            case GatewayRequestType.GetLatestNewsArticlesBySymbols:
              data = generateOfflineResponse('gateway/newsForSymbol');
              break;

            case GatewayRequestType.MarketDataSummary: {
              let staticData = require('../../__offline-data__/gateway/md/chart-1D5m.json');
              if (requestData?.bucket === GWMarketDataSummaryBucket.FiveYears) {
                staticData = require('../../__offline-data__/market-data/AAPL_5Y.json');
              }

              const mockData = (
                MOCK.TRADING_MODE === 'static'
                  ? staticData
                  : generateOHLCVSequence(
                    'AAPL',
                    [ 100.01, 200.01 ],
                    [ 100.01, 200.01 ],
                    [ 100.01, 200.01 ],
                    [ 100.01, 200.01 ],
                    [ 100, 1000000 ],
                    MOCKED_CHART_POINTS,
                  )
              );
              const smallChartData: any = {};
              symbols?.forEach(item => { smallChartData[item] = mockData.rows; });

              updateSmallChartDataCache(smallChartData, '');
              updateBigChartCacheOnLoadChartDataCompleted({ symbol, bucket: requestData?.bucket, data: mockData.rows }, '');
              break;
            }

            case GatewayRequestType.NewOrderRequest: {
              if (MOCK.TRADING_NEW_ORDER === 'accepted') {
                target.dispatch(sorOrderEvent(generateOfflineResponse('gateway/newOrderAccepted')));
              } else {
                target.dispatch(sorOrderEvent(generateOfflineResponse('gateway/newOrderRejected')));
              }
              break;
            }

            case GatewayRequestType.InstrumentSnapshot: {
              data = generateOfflineResponse('gateway/instrumentSnapshot');
              const parsedData = parseRows(data.rows, data);
              const parsedSymbols = parsedData.map(({ sym }: { sym: string }) => sym);
              requestData.symbols.forEach((currentSymbol: string) => {
                if (!parsedSymbols.includes(currentSymbol)) {
                  const count = parsedData.length;
                  const randomIndex = Math.round(Math.random() * count);
                  // duplicate some other symbol data to fill in missing ones
                  parsedData.push({
                    ...parsedData[randomIndex < count ? randomIndex : count - 1],
                    sym: currentSymbol,
                  });
                }
              });
              data = dataArrayToMap(parsedData) as InstrumentSnapshotMap;
              updateInstrumentSnapshotCache(data, '');
              updateStatsCache(data);
              updateTradePriceCacheFromInstrumentSnapshot(data);
              break;
            }

            default:
              console.warn(`[${target.name}] ${requestName} (${requestType}) - unhandled request`);
              break;
          }
          break;
        }

        case 'get': {
          const { data: resultsData, cacheData } = args[0] as any;
          const { queryCase: currentCase } = cacheData;
          const asGWRawData = [ { rows: resultsData } ];

          console.debug(`[${target.name}] RESULTS REQUEST '${GWQueryCase[currentCase]}'`, args);
          return newPromiseWithReduxCacheData(asGWRawData); // eslint-disable-line consistent-return
        }

        case 'isConnected': {
          // eslint-disable-next-line consistent-return
          return true;
        }

        default:
          throw new Error(`[${target.name}] '${prop}' not implemented in gatewayProxy`);
      }
    });

    return (...args: any[]) => func.apply(target, args);
  },
};

export class OfflineGatewayProxy {
  constructor(dispatch?: any) {
    // eslint-disable-next-line no-constructor-return
    return new Proxy(new GatewayMock(dispatch), gatewayProxy);
  }
}
